// web_components/wcNotification/wcNotification.ts

import { WebComponent_Base } from '../WebComponent_Base';
import { renderProductHTML } from './wcNotification_Renderer';
import { addEventListener } from '../../utils/events';
import { removeSkeletonLoading } from '../../utils/SkeletonLoading';
import { WebComponent_Utils } from '../WebComponent_Utils';

/**
 * A custom web component for displaying Notifications.
 */
export class WcNotification extends WebComponent_Base {
	public dataAttributes: any;
	public instanceId: string;
	private timeoutId?: number;
	private handleKeydown!: (event: KeyboardEvent) => void;

	constructor() {
		super();
		this.dataAttributes = {};
		this.instanceId = WebComponent_Utils.generateUniqueId();
		this.handleKeydown = this.handleKeydownEvent.bind(this);
	}

	/**
	 * Gets the name of the component.
	 *
	 * This getter overrides the base class implementation to return the name
	 * of the component. This value is used for logging and
	 * identifying the component in error messages or other diagnostics.
	 *
	 * @returns {string} The name of the component`.
	 */
	protected get componentName(): string {
		return 'WcNotification';
	}

	/**
	 * Retrieves data attributes.
	 * Called from the BaseComponent after stylesheets are attached.
	 */
	protected getComponentData(): void {
		this.getData();
	}

	/**
	 * Lifecycle hook called when the component is connected to the DOM.
	 */
	protected onConnected(): void {}

	/**
	 * Lifecycle hook called when the component is removed from the DOM.
	 */
	protected onDisconnected(): void {}

	/**
	 * Transfers data attributes from the light DOM elements to this.dataAttributes.
	 * This includes data-css for styling, store ids, product type, variables etc.
	 */
	private getData(): any {
		// Variables
		const variables = this.getAttribute('data-variables');
		if (variables) {
			try {
				this.dataAttributes['variables'] = JSON.parse(variables);
			} catch (e) {
				console.error(
					'wcNotification Component: Invalid JSON in data-variables attribute:',
					e,
				);
				this.dataAttributes['variables'] = null;
			}
		}
	}

	/**
	 * Perform additional setup after initialization.
	 */
	protected afterInitialize() {
		removeSkeletonLoading(this);
		this.autoRemove();
	}

	/**
	 * Renders the component based on the provided `dataAttributes` and `instanceId`.
	 * Appends the generated product content directly to the shadow DOM, after the attached styles.
	 *
	 * @protected
	 * @returns {HTMLElement | null} A reference to the first child of the appended content in the shadow DOM,
	 *                               or `null` if no content was appended.
	 */
	protected render(): HTMLElement | null {
		// Generate the content as a string
		const renderedHTML: string = renderProductHTML(this.dataAttributes, this.instanceId);

		// Convert the string to a DOM node
		const template = document.createElement('template');
		template.innerHTML = renderedHTML;

		// Append the content from the template to the shadow DOM
		this.shadowRoot!.appendChild(template.content.cloneNode(true));

		// Return the reference to the first child of the appended content
		return this.shadowRoot!.lastElementChild as HTMLElement | null;
	}

	/**
	 * Sets up event listeners for the notification component.
	 * This includes adding click event listeners to the close button, and keydown listeners to support keyboard interaction.
	 * Users can close the notification by clicking the close button or pressing the "Escape" key.
	 */
	protected attachListeners(): void {
		const closeButton = this.shadowRoot!.querySelector(
			'.wc-notification__close',
		) as HTMLElement;
		if (closeButton) {
			addEventListener(closeButton, 'click', this.removeNotification.bind(this));
		}
		addEventListener(this, 'keydown', this.handleKeydown as EventListener);
	}

	/**
	 * Handles the 'keydown' event to listen for the "Escape" key.
	 * When the "Escape" key is pressed, it removes the notification from the DOM.
	 * This ensures accessibility, allowing users to quickly dismiss the notification without a mouse.
	 *
	 * @param {KeyboardEvent} event - The keyboard event.
	 */
	handleKeydownEvent(event: KeyboardEvent): void {
		if (event.key === 'Escape') {
			this.removeNotification();
		}
	}

	/**
	 * Automatically removes the notification after a set timeout.
	 * This helps to ensure that notifications do not linger indefinitely, improving the user experience.
	 * The timeout duration is currently set to 5000 milliseconds (5 seconds).
	 */
	autoRemove(): void {
		this.timeoutId = window.setTimeout(() => this.removeNotification(), 5000);
	}

	/**
	 * Removes the notification from the DOM.
	 * Before removal, a CSS class is added to trigger any transition effects, such as fading out.
	 * After a short delay, the element is removed from the DOM entirely.
	 */
	removeNotification(): void {
		this.classList.remove('wc-notification--active');
		setTimeout(() => this.remove(), 1000); // Delay to allow CSS transition
	}
}

/**
 * Registers the custom element:
 * - Checks if the component has already been defined to avoid registration errors
 * - Registers the component with the browser’s CustomElementRegistry
 * - Automatically re-registers the component if the page is restored from Safari’s back-forward cache (bfcache)
 */
WebComponent_Utils.defineComponent('wc-notification', WcNotification);
